/**
 * MarketSnapshotFieldsConfigModal Component
 *
 * Modal for editing market snapshot field configuration from the workflow screen.
 * Allows users to modify which fields are tracked for market state observations.
 * Includes "Apply to future sessions" checkbox to optionally update global settings.
 *
 * Key behaviors:
 * - Manual Save/Cancel (not auto-save) - config changes are significant
 * - Checkbox always visible; default checked if draft matched global at open, unchecked if divergent
 * - "Reset to defaults" button visible when draft was divergent at modal open
 * - Alert shown when draft was divergent at modal open (not triggered by active edits)
 * - Saves to draft config first, optionally to global settings if checkbox checked
 * - Warnings (duplicate names) are non-blocking; only errors prevent save
 * - Field type is IMMUTABLE after creation - displayed as read-only Chip
 * - Enhanced delete confirmation when field has existing data in snapshots
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  SortableList,
  DragHandle,
  EmptyState,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { sessionDraftService } from '@/services/SessionDraftService';
import { marketSnapshotSettingsService } from '@/services/MarketSnapshotSettingsService';
import { computeMarketSnapshotFieldsHash } from '../utils/sessionDraftUtils';
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type {
  MarketSnapshotSettings,
  SnapshotFieldDefinition,
  SnapshotFieldType,
} from '@/types/schema/MarketSnapshotSettings';
import type { DropdownOption } from '@/types/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Snapshot field for local editing state
 */
interface SnapshotField {
  id: string;
  name: string;
  description?: string;
  type: SnapshotFieldType;
  options?: DropdownOption[];
  pinToLive: boolean;
}

/**
 * Blocking validation errors (prevent save)
 */
interface FieldValidationErrors {
  name?: string;
  options?: string;
}

/**
 * Non-blocking warnings (shown but don't prevent save)
 */
interface FieldValidationWarnings {
  name?: string;
}

/**
 * Field type display configuration
 */
type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

const FIELD_TYPE_CONFIG: Record<SnapshotFieldType, { label: string; color: ChipColor }> = {
  text: { label: 'Text', color: 'primary' },
  number: { label: 'Number', color: 'secondary' },
  dropdown: { label: 'Dropdown', color: 'warning' },
  checkbox: { label: 'Checkbox', color: 'success' },
  url: { label: 'URL', color: 'default' },
  image: { label: 'Image', color: 'danger' },
};

const FIELD_TYPE_OPTIONS: Array<{ key: SnapshotFieldType; label: string }> = [
  { key: 'text', label: 'Text' },
  { key: 'number', label: 'Number' },
  { key: 'dropdown', label: 'Dropdown' },
  { key: 'checkbox', label: 'Checkbox' },
  { key: 'url', label: 'URL' },
  { key: 'image', label: 'Image' },
];

export interface MarketSnapshotFieldsConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Session draft ID */
  draftId: string;
  /** Current field config from draft's MarketStateDraft entries */
  currentFieldConfig: MarketStateDraft['fieldConfigSnapshot'];
  /** Current global settings (fresh when modal opens) */
  currentSettings: MarketSnapshotSettings;
  /** Current market states (for delete data warning check) */
  marketStates: MarketStateDraft[];
  /** Callback on successful save */
  onSaveSuccess: (updatedFieldConfig: MarketStateDraft['fieldConfigSnapshot']) => void;
}

// ============================================================================
// Validation and Helper Functions
// ============================================================================

/**
 * Validate a single field
 * Returns separate errors (blocking) and warnings (non-blocking)
 */
function validateField(
  field: SnapshotField,
  allFields: SnapshotField[]
): { errors: FieldValidationErrors; warnings: FieldValidationWarnings } {
  const errors: FieldValidationErrors = {};
  const warnings: FieldValidationWarnings = {};
  const trimmedName = field.name.trim();

  // Name required (blocking error)
  if (!trimmedName) {
    errors.name = 'Name is required';
  } else {
    // Check for duplicate names (warning only, not blocking)
    const isDuplicate = allFields.some(
      (f) =>
        f.id !== field.id &&
        f.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      warnings.name = 'Duplicate field name';
    }
  }

  // Dropdown must have at least one option (blocking error)
  if (field.type === 'dropdown') {
    if (!field.options || field.options.length === 0) {
      errors.options = 'At least one option is required';
    } else {
      // Check for empty option labels (blocking error) - only when options exist
      const emptyLabelOptions = field.options.filter((o) => !o.label.trim());
      if (emptyLabelOptions.length > 0) {
        errors.options = 'Option labels cannot be empty';
      }
    }
  }

  return { errors, warnings };
}

/**
 * Check if any field has blocking errors (errors only, NOT warnings)
 */
function hasBlockingErrors(
  fields: SnapshotField[],
  validationErrors: Record<string, FieldValidationErrors>
): boolean {
  return fields.some((field) => {
    const fieldErrors = validationErrors[field.id];
    return fieldErrors && Object.keys(fieldErrors).length > 0;
  });
}

/**
 * Compare draft field config against current settings using hash comparison.
 * Order-insensitive detection reusing computeMarketSnapshotFieldsHash from Phase 1.
 */
function isSnapshotFieldsDivergent(
  draftFieldConfig: MarketStateDraft['fieldConfigSnapshot'],
  currentSettings: MarketSnapshotSettings
): boolean {
  // Convert draft format (fieldId) to global format (id) for hash comparison
  const draftAsGlobalFormat: SnapshotFieldDefinition[] = draftFieldConfig.map((fc) => {
    const field: SnapshotFieldDefinition = {
      id: fc.fieldId,
      name: fc.name,
      type: fc.type,
      pinToLive: fc.pinToLive,
    };
    if (fc.description !== undefined) {
      field.description = fc.description;
    }
    if (fc.options) {
      field.options = fc.options.map((o) => ({ ...o }));
    }
    return field;
  });

  const draftHash = computeMarketSnapshotFieldsHash(draftAsGlobalFormat);
  const globalHash = computeMarketSnapshotFieldsHash(currentSettings.snapshotFields);

  return draftHash !== globalHash;
}

/**
 * Check if a field has data in any snapshot
 */
function fieldHasData(fieldId: string, marketStates: MarketStateDraft[]): boolean {
  for (const state of marketStates) {
    for (const snapshot of state.snapshots) {
      if (fieldId in snapshot.data) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Count how many snapshots have data for a field
 */
function countSnapshotsWithFieldData(fieldId: string, marketStates: MarketStateDraft[]): number {
  let count = 0;
  for (const state of marketStates) {
    for (const snapshot of state.snapshots) {
      if (fieldId in snapshot.data) {
        count++;
      }
    }
  }
  return count;
}

// ============================================================================
// Component
// ============================================================================

export const MarketSnapshotFieldsConfigModal: React.FC<MarketSnapshotFieldsConfigModalProps> = ({
  isOpen,
  onClose,
  draftId,
  currentFieldConfig,
  currentSettings,
  marketStates,
  onSaveSuccess,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localFields, setLocalFields] = useState<SnapshotField[]>([]);
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, FieldValidationErrors>
  >({});
  const [validationWarnings, setValidationWarnings] = useState<
    Record<string, FieldValidationWarnings>
  >({});

  // Track whether draft was divergent from global when modal opened (snapshot, not live)
  const [wasInitiallyDivergent, setWasInitiallyDivergent] = useState(false);

  // Edit modal state
  const [editingFieldId, setEditingFieldId] = useState<string | 'new' | null>(null);
  const [editingDraft, setEditingDraft] = useState<SnapshotField | null>(null);
  const [editModalErrors, setEditModalErrors] = useState<FieldValidationErrors>({});
  const [editModalWarnings, setEditModalWarnings] = useState<FieldValidationWarnings>({});
  const [newOptionValue, setNewOptionValue] = useState('');

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteHasData, setDeleteHasData] = useState(false);
  const [deleteDataCount, setDeleteDataCount] = useState(0);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // isDirty tracking refs - capture initial state on modal open
  const initialFieldsSnapshotRef = useRef<string>('');
  const initialApplyToFutureRef = useRef<boolean>(true);
  const [isDirtyReady, setIsDirtyReady] = useState(false);

  // Initialize local state from currentFieldConfig when modal opens
  // Guard ensures we only initialize on open transition, not when props change while open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Convert fieldConfigSnapshot format (fieldId) to local format (id)
      const fieldsCopy: SnapshotField[] = currentFieldConfig.map((fc) => {
        const field: SnapshotField = {
          id: fc.fieldId,
          name: fc.name,
          type: fc.type,
          pinToLive: fc.pinToLive,
        };
        if (fc.description !== undefined) {
          field.description = fc.description;
        }
        if (fc.options) {
          field.options = fc.options.map((o) => ({ ...o }));
        }
        return field;
      });

      setLocalFields(fieldsCopy);
      setValidationErrors({});
      setValidationWarnings({});
      setIsSaving(false);
      setEditingFieldId(null);
      setEditingDraft(null);
      setEditModalErrors({});
      setEditModalWarnings({});
      setNewOptionValue('');
      setDeleteConfirmId(null);
      setDeleteHasData(false);
      setDeleteDataCount(0);

      // Compute initial divergence from draft vs global using hash comparison
      const initiallyDivergent = isSnapshotFieldsDivergent(
        currentFieldConfig,
        currentSettings
      );
      setWasInitiallyDivergent(initiallyDivergent);

      // Checkbox default: checked if draft matched global at open, unchecked if divergent
      setApplyToFuture(!initiallyDivergent);

      // Capture initial state for isDirty tracking (order-sensitive serialization)
      const initialSnapshot = fieldsCopy.map((f) => {
        const obj: Record<string, unknown> = {
          id: f.id,
          name: f.name,
          type: f.type,
          pinToLive: f.pinToLive,
        };
        if (f.description) {
          obj['description'] = f.description;
        }
        if (f.options && f.options.length > 0) {
          obj['options'] = f.options.map((o) => ({ id: o.id, label: o.label }));
        }
        return obj;
      });
      initialFieldsSnapshotRef.current = JSON.stringify(initialSnapshot);
      initialApplyToFutureRef.current = !initiallyDivergent;
      setIsDirtyReady(true);
    } else if (!isOpen && wasOpenRef.current) {
      // Reset on close to prepare for next open
      setIsDirtyReady(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, currentFieldConfig, currentSettings]);

  // ============================================================================
  // isDirty Computation
  // ============================================================================

  /**
   * Serialize fields for order-sensitive comparison.
   * Uses fixed key order and preserves array order (field reordering = dirty).
   * Options are NOT sorted (option reordering = dirty).
   */
  const serializeFieldsForComparison = useCallback((fields: SnapshotField[]): string => {
    const normalized = fields.map((f) => {
      // Fixed key order for deterministic serialization
      const obj: Record<string, unknown> = {
        id: f.id,
        name: f.name,
        type: f.type,
        pinToLive: f.pinToLive,
      };
      // Only include optional props when they have values
      if (f.description) {
        obj['description'] = f.description;
      }
      if (f.options && f.options.length > 0) {
        // Preserve option order (no sorting)
        obj['options'] = f.options.map((o) => ({ id: o.id, label: o.label }));
      }
      return obj;
    });
    return JSON.stringify(normalized);
  }, []);

  /**
   * Modal is dirty if fields or checkbox changed from initial state.
   */
  const isDirty = useMemo(() => {
    const currentSnapshot = serializeFieldsForComparison(localFields);
    const fieldsChanged = currentSnapshot !== initialFieldsSnapshotRef.current;
    const checkboxChanged = applyToFuture !== initialApplyToFutureRef.current;
    return fieldsChanged || checkboxChanged;
  }, [localFields, applyToFuture, serializeFieldsForComparison]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllFields = useCallback((fieldsToValidate: SnapshotField[]) => {
    const errors: Record<string, FieldValidationErrors> = {};
    const warnings: Record<string, FieldValidationWarnings> = {};

    fieldsToValidate.forEach((field) => {
      const result = validateField(field, fieldsToValidate);
      if (Object.keys(result.errors).length > 0) {
        errors[field.id] = result.errors;
      }
      if (Object.keys(result.warnings).length > 0) {
        warnings[field.id] = result.warnings;
      }
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    return errors;
  }, []);

  // ============================================================================
  // Field CRUD Handlers
  // ============================================================================

  const handleOpenAddModal = useCallback(() => {
    const newField: SnapshotField = {
      id: generateUUID(),
      name: '',
      type: 'text',
      description: '',
      options: [],
      pinToLive: false,
    };
    setEditingFieldId('new');
    setEditingDraft(newField);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleOpenEditModal = useCallback((field: SnapshotField) => {
    setEditingFieldId(field.id);
    // Deep copy including options (preserve existing option IDs)
    setEditingDraft({
      ...field,
      options: field.options ? field.options.map((o) => ({ ...o })) : [],
    });
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleEditDraftChange = useCallback(
    (key: keyof SnapshotField, value: string | boolean | DropdownOption[]) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;

        let updated: SnapshotField;

        // Handle type change - only allowed for new fields, clear options when switching away from dropdown
        if (key === 'type') {
          const newType = value as SnapshotFieldType;
          if (newType !== 'dropdown') {
            updated = { ...prev, type: newType, options: [] };
            setNewOptionValue(''); // Clear pending option input
          } else {
            updated = { ...prev, type: newType };
          }
        } else {
          updated = { ...prev, [key]: value };
        }

        // Validate draft
        const otherFields = editingFieldId === 'new'
          ? localFields
          : localFields.filter((f) => f.id !== editingFieldId);
        const result = validateField(updated, [...otherFields, updated]);
        setEditModalErrors(result.errors);
        setEditModalWarnings(result.warnings);

        return updated;
      });
    },
    [editingFieldId, localFields]
  );

  const handleAddOption = useCallback(() => {
    const trimmed = newOptionValue.trim();
    if (!trimmed || !editingDraft) return;

    const currentOptions = editingDraft.options ?? [];
    // Check for duplicate labels (case-insensitive)
    if (currentOptions.some((o) => o.label.toLowerCase() === trimmed.toLowerCase())) {
      return; // Don't add duplicates
    }

    // New options get new UUIDs (existing options keep their IDs)
    const newOption: DropdownOption = { id: generateUUID(), label: trimmed };
    handleEditDraftChange('options', [...currentOptions, newOption]);
    setNewOptionValue('');
  }, [newOptionValue, editingDraft, handleEditDraftChange]);

  const handleRemoveOption = useCallback((optionId: string) => {
    if (!editingDraft) return;
    const currentOptions = editingDraft.options ?? [];
    // Removing option preserves IDs of remaining options
    handleEditDraftChange('options', currentOptions.filter((o) => o.id !== optionId));
  }, [editingDraft, handleEditDraftChange]);

  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft) return;

    // Validate before saving
    const otherFields = editingFieldId === 'new'
      ? localFields
      : localFields.filter((f) => f.id !== editingFieldId);
    const result = validateField(editingDraft, [...otherFields, editingDraft]);

    if (Object.keys(result.errors).length > 0) {
      setEditModalErrors(result.errors);
      setEditModalWarnings(result.warnings);
      return;
    }

    // Update local fields
    let updated: SnapshotField[];
    if (editingFieldId === 'new') {
      updated = [...localFields, editingDraft];
    } else {
      updated = localFields.map((f) =>
        f.id === editingFieldId ? editingDraft : f
      );
    }

    setLocalFields(updated);
    validateAllFields(updated);

    // Close modal
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, [editingDraft, editingFieldId, localFields, validateAllFields]);

  const handleCancelEditModal = useCallback(() => {
    setEditingFieldId(null);
    setEditingDraft(null);
    setEditModalErrors({});
    setEditModalWarnings({});
    setNewOptionValue('');
  }, []);

  const handleDeleteClick = useCallback((id: string) => {
    // Check for existing data
    const hasData = fieldHasData(id, marketStates);
    const dataCount = hasData ? countSnapshotsWithFieldData(id, marketStates) : 0;

    setDeleteConfirmId(id);
    setDeleteHasData(hasData);
    setDeleteDataCount(dataCount);
  }, [marketStates]);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmId) return;

    const updated = localFields.filter((f) => f.id !== deleteConfirmId);
    setLocalFields(updated);

    // Clear validation for deleted field
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });
    setValidationWarnings((prev) => {
      const next = { ...prev };
      delete next[deleteConfirmId];
      return next;
    });

    // Re-validate remaining fields (duplicate warnings may clear)
    validateAllFields(updated);

    setDeleteConfirmId(null);
    setDeleteHasData(false);
    setDeleteDataCount(0);
  }, [deleteConfirmId, localFields, validateAllFields]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
    setDeleteHasData(false);
    setDeleteDataCount(0);
  }, []);

  const handleReorder = useCallback(
    (reorderedFields: SnapshotField[]) => {
      setLocalFields(reorderedFields);
      // Re-run validation for duplicate-name warnings
      validateAllFields(reorderedFields);
    },
    [validateAllFields]
  );

  // ============================================================================
  // Reset to Defaults Handler
  // ============================================================================

  const handleResetToDefaults = useCallback(() => {
    // Deep copy from currentSettings.snapshotFields
    const fieldsCopy: SnapshotField[] = currentSettings.snapshotFields.map((f) => {
      const field: SnapshotField = {
        id: f.id,
        name: f.name,
        type: f.type,
        pinToLive: f.pinToLive,
      };
      if (f.description !== undefined) {
        field.description = f.description;
      }
      if (f.options) {
        field.options = f.options.map((o) => ({ ...o }));
      }
      return field;
    });

    setLocalFields(fieldsCopy);
    // Clear stale errors/warnings
    setValidationErrors({});
    setValidationWarnings({});
    // Set checkbox to checked after reset (user explicitly chose to match defaults)
    setApplyToFuture(true);
    // Clear divergence warning since user explicitly chose to match defaults
    setWasInitiallyDivergent(false);
  }, [currentSettings]);

  // ============================================================================
  // Save Handler
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Guard: currentSettings must be available
    if (!currentSettings) {
      showToast({
        title: 'Cannot save',
        description: 'Settings are not available. Please try again.',
        color: 'danger',
      });
      return;
    }

    // Validate all fields - block on errors only, not warnings
    const currentErrors = validateAllFields(localFields);
    if (hasBlockingErrors(localFields, currentErrors)) {
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Build SnapshotFieldDefinition[] from localFields
      // Omit undefined optional fields for exactOptionalPropertyTypes
      const snapshotFieldDefinitions: SnapshotFieldDefinition[] = localFields.map((f) => {
        const field: SnapshotFieldDefinition = {
          id: f.id,
          name: f.name,
          type: f.type,
          pinToLive: f.pinToLive,
        };
        if (f.description) {
          field.description = f.description;
        }
        if (f.options && f.options.length > 0) {
          field.options = f.options.map((o) => ({ ...o }));
        }
        return field;
      });

      // Convert to fieldConfigSnapshot format (fieldId, not id)
      const fieldConfigSnapshot: MarketStateDraft['fieldConfigSnapshot'] = localFields.map((f) => {
        const entry: MarketStateDraft['fieldConfigSnapshot'][number] = {
          fieldId: f.id,
          name: f.name,
          type: f.type,
          pinToLive: f.pinToLive,
        };
        if (f.description) {
          entry.description = f.description;
        }
        if (f.options && f.options.length > 0) {
          entry.options = f.options.map((o) => ({ ...o }));
        }
        return entry;
      });

      // Step 1: Update draft config (all MarketStateDraft entries)
      const draftSuccess = await sessionDraftService.updateMarketStatesFieldConfig(
        draftId,
        fieldConfigSnapshot
      );

      if (!draftSuccess) {
        showToast({
          title: 'Save failed',
          description: 'Session draft not found or invalid.',
          color: 'danger',
        });
        setIsSaving(false);
        return;
      }

      // Step 2: If checkbox checked, update global settings
      let globalUpdateSucceeded = true;
      if (applyToFuture) {
        try {
          await marketSnapshotSettingsService.updateSnapshotFields(snapshotFieldDefinitions);
        } catch (err) {
          console.error('Failed to update global settings:', err);
          globalUpdateSucceeded = false;
          // Flip checkbox to false so UI reflects divergence
          setApplyToFuture(false);
          showToast({
            title: 'Partial save',
            description:
              "Changes saved to current session, but couldn't update defaults for future sessions.",
            color: 'warning',
          });
        }
      }

      // Step 3: Ack hash logic
      if (applyToFuture) {
        if (globalUpdateSucceeded) {
          // Global update succeeded - hash the new global settings (snapshotFieldDefinitions)
          const ackHash = computeMarketSnapshotFieldsHash(snapshotFieldDefinitions);
          await sessionDraftService.setMarketSnapshotFieldsAckHash(draftId, ackHash);
        }
        // If global update failed, do NOT set ack hash, keep modal open
        if (!globalUpdateSucceeded) {
          setIsSaving(false);
          return; // Keep modal open for retry
        }
      } else {
        // Checkbox unchecked - set ack hash to current global (intentional divergence)
        const ackHash = computeMarketSnapshotFieldsHash(currentSettings.snapshotFields);
        await sessionDraftService.setMarketSnapshotFieldsAckHash(draftId, ackHash);
      }

      // Step 4: Call onSaveSuccess with updated field config
      onSaveSuccess(fieldConfigSnapshot);

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSettings,
    localFields,
    applyToFuture,
    draftId,
    validateAllFields,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  // ============================================================================
  // Field Row Renderer
  // ============================================================================

  const renderFieldRow = useCallback(
    (field: SnapshotField, dragHandleProps: DragHandleProps) => {
      const typeConfig = FIELD_TYPE_CONFIG[field.type];
      const fieldErrors = validationErrors[field.id];
      const fieldWarnings = validationWarnings[field.id];
      const hasError = fieldErrors && Object.keys(fieldErrors).length > 0;
      const hasWarning = fieldWarnings && Object.keys(fieldWarnings).length > 0;

      return (
        <div
          className={`
            rounded-lg bg-default-50 p-3
            ${dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
            ${hasError ? 'ring-1 ring-danger-300' : hasWarning ? 'ring-1 ring-warning-300' : ''}
          `}
        >
          <div className="flex items-center gap-3">
            <DragHandle dragHandleProps={dragHandleProps} />

            {/* Tappable content area */}
            <button
              type="button"
              className="flex-1 text-left min-w-0"
              onClick={() => handleOpenEditModal(field)}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium truncate ${hasError ? 'text-danger' : ''}`}>
                  {field.name || 'Unnamed field'}
                </span>
                {hasError && <span className="text-danger text-xs">*</span>}
                {hasWarning && !hasError && <span className="text-warning text-xs">!</span>}
              </div>
              {field.description && (
                <div className="text-sm text-default-500 truncate">
                  {field.description}
                </div>
              )}
            </button>

            <Chip size="sm" color={typeConfig.color} variant="flat">
              {typeConfig.label}
            </Chip>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleOpenEditModal(field)}
              aria-label={`Edit ${field.name || 'field'}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteClick(field.id)}
              aria-label={`Delete ${field.name || 'field'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      );
    },
    [validationErrors, validationWarnings, handleOpenEditModal, handleDeleteClick]
  );

  // Get field name for delete confirmation
  const deleteFieldName = deleteConfirmId
    ? localFields.find((f) => f.id === deleteConfirmId)?.name || 'this field'
    : '';

  // Check if editing an existing field (type is immutable)
  const isEditingExisting = editingFieldId !== null && editingFieldId !== 'new';

  return (
    <>
      {/* Main Config Modal */}
      <Modal
        isOpen={isOpen && editingFieldId === null && deleteConfirmId === null}
        onClose={onClose}
        placement="top"
        scrollBehavior="inside"
        size="2xl"
        isDismissable={!isSaving}
        hideCloseButton={isSaving}
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Configure Snapshot Fields</span>
          </ModalHeader>
          <ModalBody className="gap-4">
            {/* Divergent State Warning - shows if draft was divergent when modal opened */}
            {wasInitiallyDivergent && (
              <Alert
                color="warning"
                title="This draft differs from your defaults"
                description="Your current edits won't affect this warning."
                endContent={
                  <Button
                    size="sm"
                    variant="flat"
                    color="warning"
                    onPress={handleResetToDefaults}
                  >
                    Reset to defaults
                  </Button>
                }
              />
            )}

            {/* Fields List */}
            {localFields.length > 0 ? (
              <SortableList
                items={localFields}
                onReorder={handleReorder}
                renderItem={renderFieldRow}
                className="space-y-2"
              />
            ) : (
              <EmptyState
                title="No fields configured"
                description="Add fields to customize what you track for market observations."
              />
            )}

            {/* Add Field Button */}
            <Button
              variant="flat"
              color="primary"
              startContent={<Plus className="w-4 h-4" />}
              onPress={handleOpenAddModal}
              className="w-full md:w-auto"
            >
              Add Field
            </Button>

            {/* Apply to Future Sessions Checkbox - always visible */}
            <Checkbox
              isSelected={applyToFuture}
              onValueChange={setApplyToFuture}
              size="sm"
            >
              Apply to future sessions
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSave} isLoading={isSaving} isDisabled={!isDirtyReady || !isDirty || isSaving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit/Add Field Modal */}
      <Modal
        isOpen={editingFieldId !== null}
        onClose={handleCancelEditModal}
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingFieldId === 'new' ? 'Add Field' : 'Edit Field'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                {/* Field Name */}
                <Input
                  label="Field Name"
                  placeholder="e.g., Trend Direction, Key Levels"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  description={editModalWarnings.name ? (
                    <span className="text-warning">{editModalWarnings.name}</span>
                  ) : undefined}
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />

                {/* Description (optional) */}
                <Input
                  label="Description (optional)"
                  placeholder="Brief description of this field"
                  value={editingDraft.description || ''}
                  onChange={(e) => handleEditDraftChange('description', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />

                {/* Field Type - Select for new, Chip for existing */}
                {isEditingExisting ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-default-600">Field Type</span>
                    <div className="flex items-center gap-2">
                      <Chip
                        size="md"
                        color={FIELD_TYPE_CONFIG[editingDraft.type].color}
                        variant="flat"
                      >
                        {FIELD_TYPE_CONFIG[editingDraft.type].label}
                      </Chip>
                      <span className="text-xs text-default-400">
                        (cannot be changed)
                      </span>
                    </div>
                  </div>
                ) : (
                  <Select
                    label="Field Type"
                    selectedKeys={new Set([editingDraft.type])}
                    onSelectionChange={(keys) => {
                      if (keys !== 'all') {
                        const selected = Array.from(keys)[0] as SnapshotFieldType;
                        if (selected) {
                          handleEditDraftChange('type', selected);
                        }
                      }
                    }}
                  >
                    {FIELD_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.key}>{option.label}</SelectItem>
                    ))}
                  </Select>
                )}

                {/* Dropdown Options Editor */}
                {editingDraft.type === 'dropdown' && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium">
                      Dropdown Options
                      {editModalErrors.options && (
                        <span className="text-danger ml-2 font-normal">
                          {editModalErrors.options}
                        </span>
                      )}
                    </div>

                    {/* Existing Options */}
                    {editingDraft.options && editingDraft.options.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {editingDraft.options.map((option) => (
                          <Chip
                            key={option.id}
                            variant="flat"
                            onClose={() => handleRemoveOption(option.id)}
                          >
                            {option.label}
                          </Chip>
                        ))}
                      </div>
                    )}

                    {/* Add New Option */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="New option"
                        value={newOptionValue}
                        onChange={(e) => setNewOptionValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        size="sm"
                        classNames={{ base: 'flex-1' }}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={handleAddOption}
                        isDisabled={!newOptionValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}

                {/* Pin to Live Checkbox - available for all field types */}
                <Checkbox
                  isSelected={editingDraft.pinToLive}
                  onValueChange={(checked) => handleEditDraftChange('pinToLive', checked)}
                  size="sm"
                >
                  Pin to live trading dashboard
                </Checkbox>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSaveEditModal}
              isDisabled={Object.keys(editModalErrors).length > 0}
            >
              {editingFieldId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmId !== null} onClose={handleCancelDelete}>
        <ModalContent>
          <ModalHeader>Delete Field?</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete <strong>{deleteFieldName}</strong>?
            </p>
            {deleteHasData ? (
              <p className="text-danger text-sm mt-2">
                This field has data in {deleteDataCount} snapshot{deleteDataCount !== 1 ? 's' : ''}.
                Deleting will permanently remove this data.
              </p>
            ) : (
              <p className="text-default-500 text-sm mt-2">
                This action cannot be undone.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelDelete}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleConfirmDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketSnapshotFieldsConfigModal;
